import { ChevronRight, type LucideIcon } from "lucide-react";
import { Text } from "@ferrivia/ui";
import s from "./Notification.module.css";

/**
 * A pinned notice at the top of a screen: something has happened that the
 * client has not dealt with yet, and tapping the row is how they deal with it.
 *
 * Deliberately not a `Card`. Home's journey blocks are all cards in one
 * column, and a notification rendered as a fourth card reads as one of the
 * stack rather than as a notice about it — which is what the unread pin did
 * before this component existed (see product/prototypes/home-notification.md,
 * direction A). Different material is the whole point: a tinted ground, no
 * border, a glyph, a chevron.
 *
 * A real `<button>`, not a `role="button"` div: the div form this replaces had
 * no keyboard activation at all, since `role` and `tabIndex` buy the
 * announcement but not the Enter/Space handling. The accessible name comes
 * from the title and detail text — the glyph, the marker and the chevron are
 * all decoration for it.
 *
 * Lives in the client app rather than @ferrivia/ui: general in shape, one
 * consumer app, which is the middle tier in platform/CLAUDE.md. Promote it
 * the day the clinic console wants one too, not before.
 */
export interface NotificationProps {
  /** Decorative — the row's accessible name comes from `title`/`detail`. */
  icon: LucideIcon;
  /** The one line that says what happened. Carries its own count, if it has one. */
  title: string;
  /** Optional second line: who or what it is about, never what it says. */
  detail?: string;
  /**
   * Draw the unread marker on the glyph — the same `--color-danger` dot the
   * chat tab wears for the same fact (see TabBar.module.css). One signal, one
   * colour, two surfaces: if this dot ever moves off red, that one moves with
   * it.
   */
  marked?: boolean;
  onClick: () => void;
}

export function Notification({ icon: Icon, title, detail, marked = false, onClick }: NotificationProps) {
  return (
    <button type="button" className={s.root} onClick={onClick}>
      <span className={s.glyph}>
        <Icon size={20} strokeWidth={1.75} aria-hidden="true" />
        {marked ? <span className={s.marker} /> : null}
      </span>
      <span className={s.body}>
        <Text as="span" size="md" weight="medium" className={s.title}>
          {title}
        </Text>
        {detail ? (
          <Text as="span" size="sm" className={s.detail}>
            {detail}
          </Text>
        ) : null}
      </span>
      <ChevronRight size={18} strokeWidth={2} className={s.chevron} aria-hidden="true" />
    </button>
  );
}
